import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import SwapVertIcon from '@mui/icons-material/SwapVert';

import { ActionPermissions } from '../../../../../@types/permission.type';
import { Panel } from '../../../../../components/atoms/Panel';
import { RichTextEditor } from '../../../../../components/atoms/RichTextEditor';
import { PopupDialog } from '../../../../../components/molecules/PopupDialog';
import { isUserHasPermission } from '../../../../../configs/permissions';
import { useGetServiceLogsById, useHideServiceLog, useUpdateServiceLogsById } from '../../../../../queries/service-log-query';

import {
  HistoryNotesPanelProps,
  HistorySubTab,
} from './HistoryNotesPanel.props';
import { HistoryTable } from './HistoryTable';
import { AttendanceMetrics } from './AttendanceMetrics';

const tabSx = {
  fontSize: '12px',
  textTransform: 'none' as const,
  minHeight: 32,
  py: 0.5,
  px: 1.5,
};

interface LogFilter {
  key: string;
  label: string;
  codes: string[];
}

const LOG_FILTERS: LogFilter[] = [
  { key: 'all', label: 'All', codes: [] },
  { key: 'notes', label: 'Notes', codes: ['notes'] },
  { key: 'status', label: 'Status Change', codes: ['status-change', 'status'] },
  { key: 'parts', label: 'Part Actions', codes: ['part-actions-install-serialised', 'part-actions-remove-serialised', 'part-actions-replace-serialised', 'part-actions-doa-serialised', 'part-actions-clean-serialised', 'part-actions-test-serialised', 'part-actions-repair-serialised', 'part-actions-adjust-serialised'] },
  { key: 'assignment', label: 'Assignments', codes: ['assignment-events-primary', 'assignment-events-secondary', 'allocation'] },
  { key: 'attendance', label: 'Attendance', codes: ['check-in', 'check-out', 'attendance'] },
  { key: 'uploads', label: 'Uploads', codes: ['uploads'] },
  { key: 'interactions', label: 'Interactions', codes: ['interaction-events'] },
];

export const HistoryNotesPanel: React.FC<HistoryNotesPanelProps> = ({
  logs = [],
  serviceId,
  permissions,
}) => {
  const [activeTab, setActiveTab] = useState<HistorySubTab>('history');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [sortDirection, setSortDirection] = useState<'newest' | 'oldest'>('newest');

  // Edit note state
  const [editLogId, setEditLogId] = useState<number | null>(null);
  const [editNotes, setEditNotes] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const canEditNotes = isUserHasPermission(ActionPermissions.Service_Edit_Service_Log_Edit_Notes, permissions);
  const canDeleteNotes = isUserHasPermission(ActionPermissions.Service_Edit_Service_Log_Hide_Records, permissions);

  const getLogByIdQuery = useGetServiceLogsById(editLogId ?? 0);
  const updateLogMutation = useUpdateServiceLogsById();
  const hideLogMutation = useHideServiceLog();

  // When a log is selected for editing, fetch its data
  useEffect(() => {
    if (editLogId) getLogByIdQuery.refetch();
  }, [editLogId]);

  useEffect(() => {
    if (getLogByIdQuery.data) {
      setEditNotes(getLogByIdQuery.data.notes ?? '');
      setEditDialogOpen(true);
    }
  }, [getLogByIdQuery.data]);

  const handleEditNote = useCallback((logId: number) => {
    if (canEditNotes) setEditLogId(logId);
  }, [canEditNotes]);

  const handleSaveNote = useCallback(async () => {
    if (!editLogId || !serviceId) return;
    await updateLogMutation.mutateAsync({ id: editLogId, serviceId, notes: editNotes });
    setEditDialogOpen(false);
    setEditLogId(null);
    setEditNotes('');
  }, [editLogId, serviceId, editNotes, updateLogMutation]);

  const handleCloseEditDialog = useCallback((_event?: unknown, reason?: string) => {
    if (reason === 'backdropClick') return;
    setEditDialogOpen(false);
    setEditLogId(null);
    setEditNotes('');
  }, []);

  const handleDeleteNote = useCallback(async (logId: number) => {
    if (!canDeleteNotes) return;
    await hideLogMutation.mutateAsync({ id: logId, isHidden: true });
  }, [canDeleteNotes, hideLogMutation]);

  // Count logs per filter category
  const filterCounts = useMemo(() => {
    const counts: Record<string, number> = { all: logs.length };
    for (const f of LOG_FILTERS) {
      if (f.key === 'all') continue;
      counts[f.key] = logs.filter((log) => {
        const code = String(log.logTypeCode ?? log.type ?? '').toLowerCase();
        return f.codes.some((c) => code.includes(c));
      }).length;
    }
    return counts;
  }, [logs]);

  // Apply search filter
  const searchFiltered = useMemo(() => {
    if (!searchQuery) return logs;
    const q = searchQuery.toLowerCase();
    return logs.filter((log) => {
      const notes = String(log.notes ?? log.description ?? '');
      const user = String(log.userName ?? log.user ?? log.createdBy ?? '');
      return notes.toLowerCase().includes(q) || user.toLowerCase().includes(q);
    });
  }, [logs, searchQuery]);

  return (
    <Panel title="History & Notes">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {/* Sub-tabs, search, and sort */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Tabs
            value={activeTab}
            onChange={(_, val) => setActiveTab(val as HistorySubTab)}
            sx={{ minHeight: 32 }}
          >
            <Tab label="History" value="history" sx={tabSx} />
            <Tab label="Attendances" value="attendances" sx={tabSx} />
          </Tabs>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title={sortDirection === 'newest' ? 'Showing newest first' : 'Showing oldest first'}>
              <IconButton
                size="small"
                onClick={() => setSortDirection((d) => d === 'newest' ? 'oldest' : 'newest')}
                aria-label="Toggle sort direction"
                sx={{ color: 'text.secondary' }}
              >
                <SwapVertIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <TextField
              size="small"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              inputProps={{ 'aria-label': 'Search history' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: 180,
                '& .MuiOutlinedInput-root': { fontSize: '12px', height: 30 },
              }}
            />
          </Box>
        </Box>

        {/* Filter chips */}
        {activeTab === 'history' && (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {LOG_FILTERS.map((f) => {
              const count = filterCounts[f.key] ?? 0;
              if (f.key !== 'all' && count === 0) return null;
              const active = activeFilter === f.key;
              return (
                <Chip
                  key={f.key}
                  label={f.key === 'all' ? f.label : `${f.label} (${count})`}
                  size="small"
                  onClick={() => setActiveFilter(f.key)}
                  sx={{
                    height: 22,
                    fontSize: '10px',
                    fontWeight: active ? 600 : 400,
                    bgcolor: active ? 'v4.accent' : 'v4.tableEven',
                    color: active ? 'common.white' : 'text.secondary',
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.85 },
                  }}
                />
              );
            })}
          </Box>
        )}

        {/* Attendance metrics (only on attendances tab) */}
        {activeTab === 'attendances' && (
          <AttendanceMetrics logs={searchFiltered} />
        )}

        {/* History table */}
        <HistoryTable
          logs={searchFiltered}
          filterType={activeTab === 'attendances' ? 'attendance' : activeFilter}
          sortDirection={sortDirection}
          onEditNote={handleEditNote}
          onDeleteNote={handleDeleteNote}
          canEditNotes={canEditNotes}
          canDeleteNotes={canDeleteNotes}
        />
      </Box>

      {/* Edit Note Dialog */}
      <PopupDialog
        title="Edit Note"
        isOpen={editDialogOpen}
        width="85%"
        maxWidth="800px"
        onClose={handleCloseEditDialog}
        onSave={handleSaveNote}
      >
        <RichTextEditor
          value={editNotes}
          onValueChange={(value: string) => setEditNotes(value)}
        />
      </PopupDialog>
    </Panel>
  );
};
